import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query';

import {
  QueryOption,
  StructureStatesResponse,
  StatesWithCountryResponse,
  ZonesResponse,
  RegionSuburbsResponse,
} from '../@types';
import { StatesKey, StatesWithCountryKey, ZonesKey } from '../@types/address.type';
import {
  BulkUpdateRegionsRequest,
  BulkUpdateRegionsResponse,
  CreateRegionRequest,
  CreateRegionResponse,
  RebuildNodeZonesResponse,
  Region,
  RegionListResponse,
  RenameZoneRequest,
  RenameZoneResponse,
  UpdateRegionRequest,
  UpdateRegionResponse,
  ZoneNodeCountResponse,
  ZoneServiceCountResponse,
  ZoneSummaryResponse,
  ZoneUserCountResponse,
} from '../@types/geosocial.type';
import {
  bulkUpdateRegions,
  createRegion,
  getRegionById,
  getRegionsByZone,
  getRegionsList,
  getStates,
  getStateWithCountryData,
  getSuburbsByPostcode,
  getZoneNodeCount,
  getZones,
  getZoneServiceCount,
  getZoneSummary,
  getZoneUserCount,
  rebuildNodeZones,
  renameZone,
  updateRegion,
} from '../api/geosocial';
import {
  RegionKey,
  RegionsByZoneKey,
  RegionsListKey,
  ZoneNodeCountKey,
  ZoneServiceCountKey,
  ZoneSummaryKey,
  ZoneUserCountKey,
} from '../configs/geosocial';

export const useGetStates = (): UseQueryResult<StructureStatesResponse[]> => {
  return useQuery({
    queryKey: [StatesKey],
    queryFn: () => getStates(),
    staleTime: 5 * 60 * 60 * 1000,
  });
};

export const useGetStateWithCountryData = (
  stateId: number
): UseQueryResult<StatesWithCountryResponse> => {
  return useQuery({
    queryKey: [StatesWithCountryKey, stateId],
    queryFn: () => getStateWithCountryData(stateId),
  });
};

export const useGetZones = (): UseQueryResult<ZonesResponse[]> => {
  return useQuery({
    queryKey: [ZonesKey],
    queryFn: () => getZones(),
  });
};

export const useGetSuburbsByPostcode = (): UseMutationResult<
  RegionSuburbsResponse[],
  Error,
  string
> => {
  return useMutation({
    mutationFn: (postcode: string) => getSuburbsByPostcode(postcode),
  });
};

/**
 * Every region and zone write can change which zone a node or service belongs
 * to, and zones themselves are just `DISTINCT geoName` over regions. So a write
 * invalidates the region keys, the zone-count keys, and `ZonesKey` — the last of
 * which is read by five screens outside this feature (Users > Zones, the
 * Services filter, Allocation Rules, the Replenishment report filter and Client
 * region overrides).
 */
const invalidateGeosocialWrites = async (
  queryClient: ReturnType<typeof useQueryClient>
): Promise<void> => {
  await Promise.all(
    [
      RegionsListKey,
      RegionsByZoneKey,
      RegionKey,
      ZonesKey,
      ZoneNodeCountKey,
      ZoneServiceCountKey,
      ZoneUserCountKey,
      ZoneSummaryKey,
    ].map((key) => queryClient.invalidateQueries({ queryKey: [key] }))
  );
};

/**
 * `enabled` is opt-out only: it defaults to true so the list screens are
 * unaffected. Pass false when there is nothing to filter on — an empty filter
 * model fetches the first page of *every* region, which is a wasted request for
 * a caller that only wants one postcode.
 */
export const useGetRegionsList = (
  queryOptions: QueryOption,
  enabled = true
): UseQueryResult<RegionListResponse> => {
  return useQuery({
    queryKey: [
      RegionsListKey,
      queryOptions.page,
      queryOptions.pageSize,
      queryOptions.filterModel,
      queryOptions.sortModel,
    ],
    queryFn: () => getRegionsList(queryOptions),
    enabled,
  });
};

export const useGetRegionsByZone = (geoName: string): UseQueryResult<Region[]> => {
  return useQuery({
    queryKey: [RegionsByZoneKey, geoName],
    queryFn: () => getRegionsByZone(geoName),
    enabled: !!geoName,
  });
};

/** Resolves `null` for an unknown id rather than erroring, because the BFF
 *  answers `200 null` for a region that does not exist. */
export const useGetRegionById = (id: number | null): UseQueryResult<Region | null> => {
  return useQuery({
    queryKey: [RegionKey, id],
    queryFn: () => getRegionById(id as number),
    enabled: id !== null,
  });
};

export const useCreateRegion = (): UseMutationResult<
  CreateRegionResponse,
  Error,
  CreateRegionRequest
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateRegionRequest) => createRegion(data),
    onSuccess: () => invalidateGeosocialWrites(queryClient),
  });
};

export const useUpdateRegion = (): UseMutationResult<
  UpdateRegionResponse,
  Error,
  { id: number; data: UpdateRegionRequest }
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateRegionRequest }) => updateRegion(id, data),
    onSuccess: () => invalidateGeosocialWrites(queryClient),
  });
};

export const useBulkUpdateRegions = (): UseMutationResult<
  BulkUpdateRegionsResponse,
  Error,
  BulkUpdateRegionsRequest
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: BulkUpdateRegionsRequest) => bulkUpdateRegions(payload),
    onSuccess: () => invalidateGeosocialWrites(queryClient),
  });
};

export const useGetZoneNodeCount = (geoName: string): UseQueryResult<ZoneNodeCountResponse> => {
  return useQuery({
    queryKey: [ZoneNodeCountKey, geoName],
    queryFn: () => getZoneNodeCount(geoName),
    enabled: !!geoName,
  });
};

export const useGetZoneServiceCount = (
  geoName: string
): UseQueryResult<ZoneServiceCountResponse> => {
  return useQuery({
    queryKey: [ZoneServiceCountKey, geoName],
    queryFn: () => getZoneServiceCount(geoName),
    enabled: !!geoName,
  });
};

export const useGetZoneUserCount = (geoName: string): UseQueryResult<ZoneUserCountResponse> => {
  return useQuery({
    queryKey: [ZoneUserCountKey, geoName],
    queryFn: () => getZoneUserCount(geoName),
    enabled: !!geoName,
  });
};

export const useGetZoneSummary = (geoName: string): UseQueryResult<ZoneSummaryResponse> => {
  return useQuery({
    queryKey: [ZoneSummaryKey, geoName],
    queryFn: () => getZoneSummary(geoName),
    enabled: !!geoName,
  });
};

export const useRenameZone = (): UseMutationResult<
  RenameZoneResponse,
  Error,
  RenameZoneRequest
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: RenameZoneRequest) => renameZone(payload),
    onSuccess: () => invalidateGeosocialWrites(queryClient),
  });
};

export const useRebuildNodeZones = (): UseMutationResult<RebuildNodeZonesResponse, Error, void> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => rebuildNodeZones(),
    onSuccess: () => invalidateGeosocialWrites(queryClient),
  });
};
